import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../config/config.service';
import { Observable } from 'rxjs';

// Imports de Types
import {
  Provedor,
  ProvedorConexao,
  ProvedorEndpoint,
  Processo,
  Recurso,
  Comando,
  ComandoMapeamento,
  PublicacaoConfig,
  CreateProvedorCompleto,
  ExecutarProcessoResult
} from '../types/smartinteg';

@Injectable({
  providedIn: 'root'
})
export class SmartintegService {

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService
  ) { }

  // ========================================
  // PROVEDORES
  // ========================================

  /**
   * Criar provedor completo (Provedor + Conexão + Credenciais)
   * Endpoint composto que cria tudo de uma vez
   */
  criarProvedorCompleto(data: CreateProvedorCompleto): Observable<Provedor> {
    return this.httpClient.post<Provedor>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedores/completo`,
      data
    );
  }

  /**
   * Listar todos os provedores
   */
  listarProvedores(): Observable<Provedor[]> {
    return this.httpClient.get<Provedor[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedores`
    );
  }

  /**
   * Buscar provedor por ID
   */
  buscarProvedor(id: string): Observable<Provedor> {
    return this.httpClient.get<Provedor>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedores/${id}`
    );
  }

  /**
   * Atualizar provedor
   */
  atualizarProvedor(data: Provedor): Observable<Provedor> {
    return this.httpClient.put<Provedor>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedores`,
      data
    );
  }

  /**
   * Deletar provedor
   */
  deletarProvedor(id: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedores/${id}`
    );
  }

  // ========================================
  // CONEXÕES
  // ========================================

  /**
   * Listar conexões por provedor
   */
  listarConexoesPorProvedor(provedorId: string): Observable<ProvedorConexao[]> {
    return this.httpClient.get<ProvedorConexao[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-conexoes/provedor/${provedorId}`
    );
  }

  /**
   * Criar nova conexão
   */
  criarConexao(data: any): Observable<ProvedorConexao> {
    return this.httpClient.post<ProvedorConexao>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-conexoes`,
      data
    );
  }

  /**
   * Atualizar conexão
   */
  atualizarConexao(data: any): Observable<ProvedorConexao> {
    return this.httpClient.put<ProvedorConexao>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-conexoes`,
      data
    );
  }

  /**
   * Deletar conexão
   */
  deletarConexao(id: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-conexoes/${id}`
    );
  }

  // ========================================
  // ENDPOINTS
  // ========================================

  /**
   * Listar endpoints por conexão
   */
  listarEndpointsPorConexao(conexaoId: string): Observable<ProvedorEndpoint[]> {
    return this.httpClient.get<ProvedorEndpoint[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-endpoints/conexao/${conexaoId}`
    );
  }

  /**
   * Criar novo endpoint
   */
  criarEndpoint(data: any): Observable<ProvedorEndpoint> {
    return this.httpClient.post<ProvedorEndpoint>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-endpoints`,
      data
    );
  }

  /**
   * Atualizar endpoint
   */
  atualizarEndpoint(data: any): Observable<ProvedorEndpoint> {
    return this.httpClient.put<ProvedorEndpoint>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-endpoints`,
      data
    );
  }

  /**
   * Deletar endpoint
   */
  deletarEndpoint(id: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/provedor-endpoints/${id}`
    );
  }

  // ========================================
  // PROCESSOS
  // ========================================

  /**
   * Listar todos os processos
   */
  listarProcessos(): Observable<Processo[]> {
    return this.httpClient.get<Processo[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processos`
    );
  }

  /**
   * Buscar processo por ID
   */
  buscarProcesso(id: string): Observable<Processo> {
    return this.httpClient.get<Processo>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processos/${id}`
    );
  }

  /**
   * Criar novo processo
   */
  criarProcesso(data: any): Observable<Processo> {
    return this.httpClient.post<Processo>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processos`,
      data
    );
  }

  /**
   * Atualizar processo
   */
  atualizarProcesso(data: any): Observable<Processo> {
    return this.httpClient.put<Processo>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processos`,
      data
    );
  }

  /**
   * Deletar processo
   */
  deletarProcesso(id: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processos/${id}`
    );
  }

  // ========================================
  // RECURSOS
  // ========================================

  /**
   * Listar todos os recursos
   */
  listarRecursos(): Observable<Recurso[]> {
    return this.httpClient.get<Recurso[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos`
    );
  }

  /**
   * Listar recursos por processo
   */
  listarRecursosPorProcesso(processoId: string): Observable<Recurso[]> {
    return this.httpClient.get<Recurso[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/processo/${processoId}`
    );
  }

  /**
   * Buscar recurso por ID
   */
  buscarRecurso(id: number): Observable<Recurso> {
    return this.httpClient.get<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/${id}`
    );
  }

  /**
   * Criar recurso genérico
   */
  criarRecurso(data: any): Observable<Recurso> {
    return this.httpClient.post<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos`,
      data
    );
  }

  /**
   * Atualizar recurso
   */
  atualizarRecurso(data: any): Observable<Recurso> {
    return this.httpClient.put<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos`,
      data
    );
  }

  /**
   * Deletar recurso
   */
  deletarRecurso(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/${id}`
    );
  }

  /**
   * Reordenar recurso
   */
  reordenarRecurso(id: number, novaOrdem: number): Observable<void> {
    return this.httpClient.put<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/${id}/reordenar?novaOrdem=${novaOrdem}`,
      null
    );
  }

  // ========================================
  // COMANDOS
  // ========================================

  /**
   * Listar todos os comandos
   */
  listarComandos(): Observable<Comando[]> {
    return this.httpClient.get<Comando[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comandos`
    );
  }

  /**
   * Listar comandos por recurso
   */
  listarComandosPorRecurso(recursoId: number): Observable<Comando[]> {
    return this.httpClient.get<Comando[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comandos/recurso/${recursoId}`
    );
  }

  /**
   * Criar novo comando
   */
  criarComando(data: any): Observable<Comando> {
    return this.httpClient.post<Comando>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comandos`,
      data
    );
  }

  /**
   * Atualizar comando
   */
  atualizarComando(data: any): Observable<Comando> {
    return this.httpClient.put<Comando>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comandos`,
      data
    );
  }

  /**
   * Deletar comando
   */
  deletarComando(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comandos/${id}`
    );
  }

  // ========================================
  // MAPEAMENTOS
  // ========================================

  /**
   * Listar todos os mapeamentos
   */
  listarMapeamentos(): Observable<ComandoMapeamento[]> {
    return this.httpClient.get<ComandoMapeamento[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comando-mapeamentos`
    );
  }

  /**
   * Listar mapeamentos por recurso
   */
  listarMapeamentosPorRecurso(recursoId: number): Observable<ComandoMapeamento[]> {
    return this.httpClient.get<ComandoMapeamento[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comando-mapeamentos/recurso/${recursoId}`
    );
  }

  /**
   * Criar novo mapeamento
   */
  criarMapeamento(data: any): Observable<ComandoMapeamento> {
    return this.httpClient.post<ComandoMapeamento>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comando-mapeamentos`,
      data
    );
  }

  /**
   * Atualizar mapeamento
   */
  atualizarMapeamento(data: any): Observable<ComandoMapeamento> {
    return this.httpClient.put<ComandoMapeamento>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comando-mapeamentos`,
      data
    );
  }

  /**
   * Deletar mapeamento
   */
  deletarMapeamento(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/comando-mapeamentos/${id}`
    );
  }

  // ========================================
  // PUBLICAÇÕES
  // ========================================

  /**
   * Listar todas as publicações
   */
  listarPublicacoes(): Observable<PublicacaoConfig[]> {
    return this.httpClient.get<PublicacaoConfig[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/publicacao-configs`
    );
  }

  /**
   * Listar publicações por recurso
   */
  buscarPublicacaoPorRecurso(recursoId: number): Observable<PublicacaoConfig[]> {
    return this.httpClient.get<PublicacaoConfig[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/publicacao-configs/recurso/${recursoId}`
    );
  }

  /**
   * Criar nova publicação
   */
  criarPublicacao(data: any): Observable<PublicacaoConfig> {
    return this.httpClient.post<PublicacaoConfig>(
      `${this.configService.getApiUrlRoot()}/smartinteg/publicacao-configs`,
      data
    );
  }

  /**
   * Atualizar publicação
   */
  atualizarPublicacao(data: any): Observable<PublicacaoConfig> {
    return this.httpClient.put<PublicacaoConfig>(
      `${this.configService.getApiUrlRoot()}/smartinteg/publicacao-configs`,
      data
    );
  }

  /**
   * Deletar publicação
   */
  deletarPublicacao(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/publicacao-configs/${id}`
    );
  }

  // ========================================
  // EXECUÇÃO DE PROCESSOS
  // ========================================

  /**
   * Executar processo
   */
  executarProcesso(processoId: string): Observable<ExecutarProcessoResult> {
    return this.httpClient.post<ExecutarProcessoResult>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processo-executor/execute/${processoId}`,
      null
    );
  }

  /**
   * Alterar status (ativo/inativo) do processo
   */
  alterarStatusProcesso(processoId: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.configService.getApiUrlRoot()}/smartinteg/processos/alterar-status/${processoId}`,
      null
    );
  }

  // ========================================
  // ENDPOINTS COMPOSTOS PARA WIZARD
  // ========================================

  /**
   * Criar recurso de importação completo (recurso + comando)
   */
  criarRecursoImportacaoCompleto(data: any): Observable<Recurso> {
    return this.httpClient.post<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/importacao-completo`,
      data
    );
  }

  /**
   * Criar recurso de processamento completo (recurso + mapeamento)
   */
  criarRecursoProcessamentoCompleto(data: any): Observable<Recurso> {
    return this.httpClient.post<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/processamento-completo`,
      data
    );
  }

  /**
   * Criar recurso de visualização completo (recurso + mapeamento)
   */
  criarRecursoVisualizacaoCompleto(data: any): Observable<Recurso> {
    return this.httpClient.post<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/visualizacao-completo`,
      data
    );
  }

  /**
   * Criar recurso de exportação completo (recurso + comando)
   */
  criarRecursoExportacaoCompleto(data: any): Observable<Recurso> {
    return this.httpClient.post<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/exportacao-completo`,
      data
    );
  }

  /**
   * Criar recurso de publicação completo (recurso + config)
   */
  criarRecursoPublicacaoCompleto(data: any): Observable<Recurso> {
    return this.httpClient.post<Recurso>(
      `${this.configService.getApiUrlRoot()}/smartinteg/recursos/publicacao-completo`,
      data
    );
  }
}
